import {
  useGenericRefinement,
  BaseRefinementItem,
  RefinementConfig,
  RawRefinementItem,
} from '../../Algolia/useGenericRefinement';

// Direct mapping for Kincaid Grade levels (1-12)
export const KINCAID_GRADE_MAP: Record<string, number> = {
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  '10': 10,
  '11': 11,
  '12': 12,
};

export interface KincaidGradeRefinementItem extends BaseRefinementItem {
  gradeLevel: number;
  gradeName: string;
}

export interface KincaidGradeMapping {
  gradeMap: typeof KINCAID_GRADE_MAP;
}

export interface KincaidGradeRefinementResult {
  items: KincaidGradeRefinementItem[];
  refine: (value: string) => void;
  refineMultiple: (values: string[]) => void;
  clearRefinements: () => void;
  isSearchStalled: boolean;
  canRefine: boolean;
  getCountForLevel: (level: number) => number;
  getCountForValue: (value: string) => number;
  getTotalCount: () => number;
}

// Configuration for KincaidGrade refinement
const kincaidGradeConfig: RefinementConfig<
  KincaidGradeRefinementItem,
  KincaidGradeMapping
> = {
  attribute: 'metadata.flesch_kincaid',
  mapping: {
    gradeMap: KINCAID_GRADE_MAP,
  },

  validateItem: (item: RawRefinementItem, mapping: KincaidGradeMapping): boolean => {
    const gradeLevel = mapping.gradeMap[item.value];
    return !!(gradeLevel && gradeLevel >= 1 && gradeLevel <= 12);
  },

  transformItem: (
    item: RawRefinementItem,
    mapping: KincaidGradeMapping,
  ): KincaidGradeRefinementItem => {
    const gradeLevel = mapping.gradeMap[item.value] || 0;
    const gradeName = `Grade ${item.value}`;

    return {
      label: gradeName,
      value: item.value,
      count: item.count,
      isRefined: item.isRefined,
      gradeLevel,
      gradeName,
    };
  },

  sortItems: (a: KincaidGradeRefinementItem, b: KincaidGradeRefinementItem): number =>
    a.gradeLevel - b.gradeLevel,

  getters: {
    getCountForLevel: (items: KincaidGradeRefinementItem[], level: number): number => {
      const item = items.find((gradeItem) => gradeItem.gradeLevel === level);
      return item?.count || 0;
    },
    getCountForValue: (items: KincaidGradeRefinementItem[], value: string): number => {
      const item = items.find((gradeItem) => gradeItem.value === value);
      return item?.count || 0;
    },
  },
};

/**
 * Custom hook for Kincaid Grade refinements
 * Provides Algolia facet data specifically formatted for Kincaid Grade levels (1-12)
 */
export const useKincaidGradeRefinement = (
  attribute = 'metadata.flesch_kincaid',
): KincaidGradeRefinementResult => {
  const config = { ...kincaidGradeConfig, attribute };
  const result =
    useGenericRefinement<KincaidGradeRefinementItem, KincaidGradeMapping>(config);

  return {
    ...result,
    getCountForLevel: result.getCountForLevel,
    getCountForValue: result.getCountForValue,
  };
};
